// ConnectorsPanel.tsx — the user's connectors: MCP servers AND plain REST APIs
// (Oct 7 2026: base URL + OpenAPI description + key). Add, connect/authorize,
// set policy per connector, per risk tier and per tool, see pending approvals.
// Talks to /api/mcp/*. Plain fetch with the session token, like the other panels.
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { HowTo, GUIDES } from './HowTo';

type Policy = 'auto' | 'ask' | 'never';
type Tier = 'read' | 'write' | 'money';

interface ToolRow { name: string; description: string; tier: Tier; policy: Policy }
type Transport = 'http' | 'stdio' | 'api';
type AuthType = 'none' | 'bearer' | 'oauth' | 'client_credentials' | 'basic' | 'query';
interface ServerRow {
  id: string; name: string; description?: string; notes?: string; transport: Transport; url?: string; command?: string; args?: string[];
  baseUrl?: string; specUrl?: string; docsUrl?: string;
  auth: { type: AuthType }; enabled: boolean; policy: { default: Policy; tools?: Record<string, Policy>; byTier?: Partial<Record<Tier, Policy>> };
  status: string; error?: string; authUrl?: string; tools: ToolRow[]; connectedAt?: number;
}
/** "Name: value" lines → headers. Blank lines and lines without a colon are skipped. */
const parseHeaderLines = (text: string): Record<string, string> | undefined => {
  const out: Record<string, string> = {};
  for (const line of text.split(/\r?\n/)) { const i = line.indexOf(':'); if (i > 0) { const k = line.slice(0, i).trim(); const v = line.slice(i + 1).trim(); if (k && v) out[k] = v; } }
  return Object.keys(out).length ? out : undefined;
};
/** Permission presets for a new connector. "Reads on their own, changes ask" is the default. */
const PERMISSIONS: Record<string, { label: string; policy: { default: Policy; byTier?: Partial<Record<Tier, Policy>> } }> = {
  reads: { label: 'Reads on their own, changes ask (recommended)', policy: { default: 'ask', byTier: { read: 'auto' } } },
  ask: { label: 'Everything asks first', policy: { default: 'ask' } },
  auto: { label: 'Everything automatic (money still asks)', policy: { default: 'auto' } },
  never: { label: 'Blocked (connect only to look)', policy: { default: 'never' } }
};
const TOOLS_SHOWN = 60;
interface Pending { id: string; server: string; tool: string; args: any; tier: Tier; reason: string; at: number; expiresAt: number }

// 🧩 Presets: the connectors we know well, one click to fill the form. Everything stays editable.
const PRESETS: Array<{ key: string; label: string; fill: Record<string, any> }> = [
  { key: '', label: 'Start from a preset…', fill: {} },
  { key: 'ms365-personal', label: 'Microsoft 365 — my personal account (Outlook, calendar, OneDrive)', fill: {
    id: 'ms365', name: 'Microsoft 365 (personal)', transport: 'stdio', command: 'npx',
    args: '-y @softeria/ms-365-mcp-server --enabled-tools login|logout|verify|account|mail|message|calendar|event|drive|file|folder|contact|todo',
    authType: 'none', permissions: 'reads',
    notes: 'My personal Microsoft account. To sign in: call the login tool, give me the URL and code it returns, then call verify-login. Reads are fine on their own; ask me before sending mail or changing anything.'
  } },
  { key: 'ms365-work', label: 'Microsoft 365 — my work account (mail, calendar, OneDrive, Teams chats, SharePoint)', fill: {
    id: 'ms365-work', name: 'Microsoft 365 (work)', transport: 'stdio', command: 'npx',
    args: '-y @softeria/ms-365-mcp-server --org-mode --enabled-tools login|logout|verify|account|mail|message|calendar|event|drive|file|folder|contact|todo|chat|teams|channel|sharepoint',
    authType: 'none', permissions: 'reads',
    notes: 'My work Microsoft 365 account. To sign in: call the login tool, give me the URL and code it returns, then call verify-login. Reads are fine on their own; ask me before sending mail, posting in Teams, or changing anything.'
  } },
  { key: 'conduit', label: 'Conduit by WYRE — MSP tools gateway (ConnectWise, Autotask, NinjaOne…)', fill: {
    id: 'conduit', name: 'Conduit', transport: 'http', url: 'https://conduit.wyre.ai/v1/mcp',
    authType: 'client_credentials', tokenEndpoint: 'https://conduit.wyre.ai/oauth/token', permissions: 'reads',
    notes: 'The MSP tool gateway. Reads are automatic; anything that changes a ticket, note, time entry or record asks first.'
  } },
  { key: 'rest', label: 'Any REST API (no MCP server) — base URL + its OpenAPI/Swagger description + key', fill: {
    transport: 'api', authType: 'bearer', permissions: 'reads',
    notes: 'What this system is, what I use it for, and anything he should never touch.'
  } }
];

const STATUS_LABEL: Record<string, string> = {
  connected: 'Connected', connecting: 'Connecting…', disconnected: 'Not connected', auth_required: 'Needs sign-in', error: 'Error', disabled: 'Off'
};

export const ConnectorsList: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { token } = useAuth();
  const headers = useCallback((json = false): Record<string, string> => ({ ...(json ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) }), [token]);
  const [servers, setServers] = useState<ServerRow[]>([]);
  const [pending, setPending] = useState<Pending[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const emptyForm = { id: '', name: '', transport: 'http' as Transport, url: '', command: '', args: '', authType: 'none' as AuthType, token: '', headerName: '', clientId: '', clientSecret: '', tokenEndpoint: '', scope: '', username: '', password: '', queryParam: 'api_key', queryValue: '', extraHeaders: '', baseUrl: '', specUrl: '', specText: '', specFileName: '', docsUrl: '', permissions: 'reads', notes: '' };
  const [form, setForm] = useState(emptyForm);
  const [toolFilter, setToolFilter] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setError(null);
      const r = await fetch('/api/mcp/servers', { headers: headers() });
      if (!r.ok) throw new Error(r.status === 503 ? 'Connectors are still starting up.' : `Could not load connectors (${r.status})`);
      const d = await r.json();
      setServers(d.servers || []); setPending(d.pending || []);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const act = async (label: string, fn: () => Promise<Response>) => {
    setBusy(label);
    try { const r = await fn(); if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `${label} failed (${r.status})`); } await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };

  const submitAdd = async () => {
    const isApi = form.transport === 'api';
    const extraHeaders = parseHeaderLines(form.extraHeaders);
    const body: any = {
      id: form.id.trim() || form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'), name: form.name.trim(), transport: form.transport,
      url: form.transport === 'http' ? form.url.trim() : undefined, command: form.transport === 'stdio' ? form.command.trim() : undefined,
      args: form.transport === 'stdio' && form.args.trim() ? form.args.trim().split(/\s+/) : undefined,
      baseUrl: isApi ? form.baseUrl.trim() || undefined : undefined, specUrl: isApi ? form.specUrl.trim() || undefined : undefined,
      specText: isApi && form.specText ? form.specText : undefined, docsUrl: isApi ? form.docsUrl.trim() || undefined : undefined,
      auth: form.authType === 'bearer' ? { type: 'bearer', token: form.token.trim(), headerName: form.headerName.trim() || undefined, extraHeaders }
        : form.authType === 'client_credentials' ? { type: 'client_credentials', clientId: form.clientId.trim(), clientSecret: form.clientSecret.trim(), tokenEndpoint: form.tokenEndpoint.trim(), scope: form.scope.trim() || undefined, extraHeaders }
        : form.authType === 'basic' ? { type: 'basic', username: form.username, password: form.password, extraHeaders }
        : form.authType === 'query' ? { type: 'query', queryParam: form.queryParam.trim(), queryValue: form.queryValue.trim(), extraHeaders }
        : { type: form.authType, extraHeaders },
      enabled: true, policy: (PERMISSIONS[form.permissions] || PERMISSIONS.reads).policy, notes: form.notes.trim() || undefined
    };
    if (!body.name || (form.transport === 'http' && !body.url) || (form.transport === 'stdio' && !body.command)) { setError('A connector needs a name and a URL (or a command).'); return; }
    if (isApi && !body.baseUrl && !body.specUrl && !body.specText) { setError('A REST API needs its base URL, or its OpenAPI/Swagger description (which names the base URL).'); return; }
    if (form.authType === 'client_credentials' && (!form.clientId.trim() || !form.clientSecret.trim() || !form.tokenEndpoint.trim())) { setError('Client-credentials auth needs the client ID, the client secret and the token endpoint.'); return; }
    if (form.authType === 'basic' && !form.username && !form.password) { setError('Basic auth needs the username and password (or key pair) the API gave you.'); return; }
    if (form.authType === 'query' && (!form.queryParam.trim() || !form.queryValue.trim())) { setError('A key in the address needs the parameter name and the key.'); return; }
    await act('add', () => fetch('/api/mcp/servers', { method: 'POST', headers: headers(true), body: JSON.stringify(body) }));
    setAdding(false); setForm(emptyForm);
  };

  const setDefaultPolicy = (s: ServerRow, p: Policy) => act('policy', () => fetch(`/api/mcp/servers/${encodeURIComponent(s.id)}/policy`, { method: 'PUT', headers: headers(true), body: JSON.stringify({ default: p }) }));
  const setTierPolicy = (s: ServerRow, tier: Tier, p: Policy | '') => act('policy', () => fetch(`/api/mcp/servers/${encodeURIComponent(s.id)}/policy`, { method: 'PUT', headers: headers(true), body: JSON.stringify({ byTier: { [tier]: p || null } }) }));
  const loadSpecFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, specText: String(reader.result || ''), specFileName: file.name }));
    reader.onerror = () => setError(`Could not read ${file.name}`);
    reader.readAsText(file);
  };
  const setToolPolicy = (s: ServerRow, tool: string, p: Policy) => act('policy', () => fetch(`/api/mcp/servers/${encodeURIComponent(s.id)}/policy`, { method: 'PUT', headers: headers(true), body: JSON.stringify({ tools: { [tool]: p } }) }));
  const connect = (s: ServerRow) => act('connect', () => fetch(`/api/mcp/servers/${encodeURIComponent(s.id)}/connect`, { method: 'POST', headers: headers() }));
  const remove = (s: ServerRow) => { if (window.confirm(`Remove the ${s.name} connector? His notes about it stay; its tokens are deleted.`)) act('remove', () => fetch(`/api/mcp/servers/${encodeURIComponent(s.id)}`, { method: 'DELETE', headers: headers() })); };
  const toggleEnabled = (s: ServerRow) => act('toggle', () => fetch('/api/mcp/servers', { method: 'POST', headers: headers(true), body: JSON.stringify({ id: s.id, name: s.name, transport: s.transport, enabled: !s.enabled, connect: !s.enabled }) }));
  const approve = (p: Pending) => act('approve', () => fetch(`/api/mcp/pending/${p.id}/approve`, { method: 'POST', headers: headers() }));
  const deny = (p: Pending) => act('deny', () => fetch(`/api/mcp/pending/${p.id}/deny`, { method: 'POST', headers: headers() }));

  return (
    <div className={embedded ? 'connectors-content embedded' : 'tab-content connectors-content'}>
      {!embedded && (
        <>
          <h3>🔌 Connectors</h3>
          <p className="panel-lede">Outside services he can use, through MCP. You decide what each one may do; everything he does through them is on the Activity ledger.</p>
          <HowTo guide={GUIDES.connectors} />
        </>
      )}

      {error && <div className="panel-error">{error} <button className="link-button" onClick={() => setError(null)}>dismiss</button></div>}

      {pending.length > 0 && (
        <div className="pending-box">
          <h4>Waiting for your yes ({pending.length})</h4>
          {pending.map(p => (
            <div key={p.id} className="pending-row">
              <div className="pending-main">
                <span className={`tier tier-${p.tier}`}>{p.tier}</span>
                <strong>{p.tool}</strong> on {servers.find(s => s.id === p.server)?.name || p.server}
                {p.reason && <div className="pending-reason">{p.reason}</div>}
                <pre className="pending-args">{JSON.stringify(p.args, null, 0).substring(0, 400)}</pre>
                <div className="pending-meta">#{p.id} · asked {new Date(p.at).toLocaleTimeString()} · expires {new Date(p.expiresAt).toLocaleTimeString()}</div>
              </div>
              <div className="pending-actions">
                <button className="btn-approve" disabled={busy !== null} onClick={() => approve(p)}>Approve</button>
                <button className="btn-deny" disabled={busy !== null} onClick={() => deny(p)}>Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? <div className="panel-muted">Loading…</div> : servers.length === 0 && !adding ? (
        <div className="panel-empty">No connectors yet. Add one to give him hands in an outside service.</div>
      ) : null}

      {servers.map(s => {
        const open = openId === s.id;
        return (
          <div key={s.id} className={`connector-card status-${s.status}`}>
            <div className="connector-head" onClick={() => setOpenId(open ? null : s.id)}>
              <span className={`status-dot ${s.status}`} title={STATUS_LABEL[s.status] || s.status} />
              <div className="connector-title">
                <strong>{s.name}</strong>
                <span className="connector-sub">{STATUS_LABEL[s.status] || s.status}{s.status === 'connected' ? ` · ${s.tools.length} tools` : ''} · policy {s.policy.default}</span>
              </div>
              <span className="chev">{open ? '▾' : '▸'}</span>
            </div>
            {s.error && !open && <div className="connector-error">{s.error}</div>}
            {open && (
              <div className="connector-body">
                {s.description && <p className="panel-muted">{s.description}</p>}
                <div className="kv"><span>{s.transport === 'api' ? 'API' : 'Address'}</span><code>{s.transport === 'api' ? (s.baseUrl || s.specUrl || 'from its description') : s.transport === 'http' ? s.url : `${s.command} ${(s.args || []).join(' ')}`}</code></div>
                {s.transport === 'api' && s.specUrl && <div className="kv"><span>Description</span><code>{s.specUrl}</code></div>}
                <div className="kv"><span>Auth</span><code>{s.auth.type}</code></div>
                {s.status === 'auth_required' && (
                  <div className="auth-box">
                    <div>He needs you to sign in to {s.name} once.</div>
                    {s.authUrl ? <a className="btn-primary" href={s.authUrl} target="_blank" rel="noreferrer">Authorize {s.name}</a> : <button className="btn-primary" onClick={() => connect(s)} disabled={busy !== null}>Start sign-in</button>}
                  </div>
                )}
                {s.error && <div className="connector-error">{s.error}</div>}
                <div className="row-actions">
                  <button onClick={() => connect(s)} disabled={busy !== null}>{s.status === 'connected' ? 'Reconnect' : 'Connect'}</button>
                  <button onClick={() => toggleEnabled(s)} disabled={busy !== null}>{s.enabled ? 'Turn off' : 'Turn on'}</button>
                  <button className="btn-danger" onClick={() => remove(s)} disabled={busy !== null}>Remove</button>
                </div>
                <div className="policy-row">
                  <label>Default policy</label>
                  <select value={s.policy.default} onChange={e => setDefaultPolicy(s, e.target.value as Policy)} disabled={busy !== null}>
                    <option value="auto">Auto — he may use tools freely</option>
                    <option value="ask">Ask — every action waits for my yes</option>
                    <option value="never">Never — blocked</option>
                  </select>
                </div>
                <div className="policy-row">
                  <label>By kind of action (overrides the default; a tool set by name overrides this)</label>
                  {(['read', 'write', 'money'] as Tier[]).map(tier => (
                    <div key={tier} className="tier-policy">
                      <span className={`tier tier-${tier}`}>{tier === 'read' ? 'reads' : tier === 'write' ? 'changes' : 'money'}</span>
                      <select value={s.policy.byTier?.[tier] || ''} onChange={e => setTierPolicy(s, tier, e.target.value as Policy | '')} disabled={busy !== null}>
                        <option value="">Use the default</option>
                        {tier !== 'money' && <option value="auto">Auto</option>}
                        <option value="ask">Ask</option>
                        <option value="never">Never</option>
                      </select>
                    </div>
                  ))}
                </div>
                {s.tools.length > 0 && (() => {
                  const q = (toolFilter[s.id] || '').trim().toLowerCase();
                  const matching = q ? s.tools.filter(t => t.name.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)) : s.tools;
                  return (
                  <div className="tools-list">
                    <h4>{s.transport === 'api' ? 'Endpoints' : 'Tools'} ({s.tools.length})</h4>
                    {s.tools.length > 12 && <input className="tool-filter" value={toolFilter[s.id] || ''} onChange={e => setToolFilter({ ...toolFilter, [s.id]: e.target.value })} placeholder={`Find among ${s.tools.length} — e.g. ticket, invoice, POST`} />}
                    {matching.length > TOOLS_SHOWN && <div className="panel-muted">Showing {TOOLS_SHOWN} of {matching.length}. Type above to find the one you want.</div>}
                    {matching.slice(0, TOOLS_SHOWN).map(t => (
                      <div key={t.name} className="tool-row">
                        <div className="tool-main">
                          <span className={`tier tier-${t.tier}`}>{t.tier}</span>
                          <strong>{t.name}</strong>
                          <div className="tool-desc">{t.description}</div>
                        </div>
                        <select value={t.policy} onChange={e => setToolPolicy(s, t.name, e.target.value as Policy)} disabled={busy !== null} title="Policy for this tool">
                          <option value="auto">Auto</option>
                          <option value="ask">Ask</option>
                          <option value="never">Never</option>
                        </select>
                      </div>
                    ))}
                  </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })}

      {adding ? (
        <div className="add-form">
          <h4>Add a connector</h4>
          <label>Preset
            <select value="" onChange={e => { const pr = PRESETS.find(x => x.key === e.target.value); if (pr && pr.key) setForm({ ...emptyForm, ...pr.fill }); }}>
              {PRESETS.map(pr => <option key={pr.key || 'none'} value={pr.key}>{pr.label}</option>)}
            </select>
          </label>
          <label>Name<input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Google Drive" /></label>
          <label>Id (short, letters and dashes)<input value={form.id} onChange={e => setForm({ ...form, id: e.target.value })} placeholder="gdrive" /></label>
          <label>Kind
            <select value={form.transport} onChange={e => setForm({ ...form, transport: e.target.value as Transport, authType: e.target.value === 'api' && form.authType === 'oauth' ? 'bearer' : form.authType })}>
              <option value="http">MCP server — hosted (https URL)</option>
              <option value="stdio">MCP server — local command</option>
              <option value="api">REST API (no MCP server needed)</option>
            </select>
          </label>
          {form.transport === 'http' && <label>MCP URL<input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://example.com/mcp" /></label>}
          {form.transport === 'stdio' && <>
            <label>Command<input value={form.command} onChange={e => setForm({ ...form, command: e.target.value })} placeholder="npx" /></label>
            <label>Arguments<input value={form.args} onChange={e => setForm({ ...form, args: e.target.value })} placeholder="-y @modelcontextprotocol/server-filesystem C:\\Users\\me\\Documents" /></label>
          </>}
          {form.transport === 'api' && <>
            <label>API description (OpenAPI / Swagger) URL — usually ends in openapi.json, swagger.json or .yaml
              <input value={form.specUrl} onChange={e => setForm({ ...form, specUrl: e.target.value })} placeholder="https://api.example.com/openapi.json" />
            </label>
            <label>…or upload the description file
              <input type="file" accept=".json,.yaml,.yml,application/json,text/yaml" onChange={e => loadSpecFile(e.target.files?.[0])} />
              {form.specFileName && <span className="panel-muted">{form.specFileName} loaded ({Math.round(form.specText.length / 1024)} KB)</span>}
            </label>
            <label>Base URL — where calls go (leave blank to use the one the description names)
              <input value={form.baseUrl} onChange={e => setForm({ ...form, baseUrl: e.target.value })} placeholder="https://api.example.com/v1" />
            </label>
            <label>Docs URL (optional — he reads it when there is no description)
              <input value={form.docsUrl} onChange={e => setForm({ ...form, docsUrl: e.target.value })} placeholder="https://developer.example.com/docs" />
            </label>
          </>}
          <label>Authentication
            <select value={form.authType} onChange={e => setForm({ ...form, authType: e.target.value as AuthType })}>
              <option value="none">None</option>
              <option value="bearer">API key / bearer token (in a header)</option>
              {form.transport === 'api' && <option value="basic">Username + password (Basic)</option>}
              {form.transport === 'api' && <option value="query">API key in the address (?api_key=…)</option>}
              <option value="client_credentials">Client ID + secret (service account, e.g. Conduit)</option>
              {form.transport !== 'api' && <option value="oauth">OAuth (sign in through the service)</option>}
            </select>
          </label>
          {form.authType === 'bearer' && <>
            <label>Key / token<input type="password" value={form.token} onChange={e => setForm({ ...form, token: e.target.value })} placeholder="stored encrypted in the vault" /></label>
            <label>Header name (blank = Authorization: Bearer …; e.g. x-api-key sends the key as-is)<input value={form.headerName} onChange={e => setForm({ ...form, headerName: e.target.value })} placeholder="x-api-key" /></label>
          </>}
          {form.authType === 'basic' && <>
            <label>Username (or public key / company+key — whatever the API's docs say)<input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="stored encrypted in the vault" /></label>
            <label>Password (or private key)<input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="stored encrypted in the vault" /></label>
          </>}
          {form.authType === 'query' && <>
            <label>Parameter name<input value={form.queryParam} onChange={e => setForm({ ...form, queryParam: e.target.value })} placeholder="api_key" /></label>
            <label>Key<input type="password" value={form.queryValue} onChange={e => setForm({ ...form, queryValue: e.target.value })} placeholder="stored encrypted in the vault" /></label>
          </>}
          {form.authType === 'client_credentials' && <>
            <label>Client ID<input value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} placeholder="svc_…" /></label>
            <label>Client secret<input type="password" value={form.clientSecret} onChange={e => setForm({ ...form, clientSecret: e.target.value })} placeholder="stored encrypted in the vault" /></label>
            <label>Token endpoint<input value={form.tokenEndpoint} onChange={e => setForm({ ...form, tokenEndpoint: e.target.value })} placeholder="https://conduit.wyre.ai/oauth/token" /></label>
            <label>Scope (optional)<input value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} placeholder="leave blank unless the service requires one" /></label>
          </>}
          <label>Extra headers (optional, one per line, stored encrypted) — e.g. ConnectWise's clientId, Autotask's ApiIntegrationCode
            <textarea rows={3} value={form.extraHeaders} onChange={e => setForm({ ...form, extraHeaders: e.target.value })} placeholder={'clientId: 1234-abcd\nApiIntegrationCode: XYZ'} />
          </label>
          <label>What he may do
            <select value={form.permissions} onChange={e => setForm({ ...form, permissions: e.target.value })}>
              {Object.entries(PERMISSIONS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
          <label>What is this for? (he reads this)<input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="My work files; the Photos folder is what I want sorted" /></label>
          <div className="row-actions">
            <button className="btn-primary" onClick={submitAdd} disabled={busy !== null}>Add and connect</button>
            <button onClick={() => setAdding(false)} disabled={busy !== null}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn-primary add-button" onClick={() => setAdding(true)}>＋ Add connector</button>
      )}
    </div>
  );
};

const ConnectorsPanel: React.FC = () => <ConnectorsList />;
export default ConnectorsPanel;
